import React, { useEffect, useState, useRef, useCallback } from 'react';
import PrimaryButton from '../common/PrimaryButton';
import ReusableTextField from '../common/ReusableTextField';
import EventService from "../../services/EventService.js";
import ZoomServices from "../../services/ZoomServices";
import GoogleMeetServices from "../../services/GoogleMeetService";
import ZohoMeetingServices from "../../services/ZohoMeetingService";
import { useEventStore } from "../../store/eventStore";
import {
  showDeleteConfirm,
  showErrorAlert,
  showSuccessAlert,
  showWarningAlert,
} from "../../utils/sweetAlertUtils";
import '../../assets/css/AddDetailsTab.css';
import FeatureFlagService, { FeatureFlags } from '../../services/FeatureFlagService';

const SESSION_TYPE = {
  WEBINAR: 'webinar',
  MEETING: 'meeting',
};

const VIDEO_PROVIDER = {
  ZOOM: 'zoom',
  ZOHO: 'zoho',
  GOOGLE: 'google',
};

const SESSION_TYPES = [
  { key: SESSION_TYPE.WEBINAR, label: 'Webinar' },
  { key: SESSION_TYPE.MEETING, label: 'Meeting' },
];

const VIDEO_PROVIDERS = [
  { key: VIDEO_PROVIDER.ZOOM, label: 'Zoom' },
  { key: VIDEO_PROVIDER.ZOHO, label: 'Zoho' },
  { key: VIDEO_PROVIDER.GOOGLE, label: 'Google Meet' },
];

const SESSION_MESSAGES = {
  EVENT_NAME_REQUIRED:
    'Please enter Event Name in Add Details before creating a session.',
  START_DATE_TIME_REQUIRED:
    'Please set Start Date and Event Time in Add Details before creating a session.',
  DURATION_REQUIRED:
    'Please set Duration (In minutes) in Add Details before creating a session.',
  NO_MEETING_LINK: (providerLabel, sessionLabel) =>
    `${providerLabel} ${sessionLabel} created, but no meeting link was returned.`,
  LINK_FILLED: (providerLabel, sessionLabel) =>
    `${providerLabel} ${sessionLabel} created. Video Conference Link has been filled.`,
  CREATE_FAILED: (providerLabel, sessionLabel) =>
    `Failed to create ${providerLabel} ${sessionLabel}. Check that ${providerLabel} is connected under Video Conf.`,
};

const extractJoinUrl = (response) =>
  response?.joinUrl ||
  response?.meetingUrl ||
  response?.join_url ||
  response?.startUrl ||
  response?.start_url ||
  "";

const FinalTouchUpTab = ({ onNext, onBack, registerValidator }) => {
  const campaignData = useEventStore((state) => state.eventDetails);
  const updateEventDetails = useEventStore((state) => state.updateEventDetails);
  const markSaved = useEventStore((state) => state.markSaved);

  // ✅ Destructure shared data
  const {
    campaignId,
    campaignName,
    startDate,
    durationInMinutes,
    eventTime,
    description,
    formFields,
    eventPriceTagLine,
    eventPrice,
    templateId,
    eventTemplateId,
    templateCategory,
    templateImage,
    templateHtml,
    templateSummary,
    videoConferenceLink,
    isDraft
  } = campaignData;


  const [saving, setSaving] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [sessionType, setSessionType] = useState(SESSION_TYPE.WEBINAR);
  const [videoProvider, setVideoProvider] = useState(VIDEO_PROVIDER.ZOOM);
  const [connectedProviders, setConnectedProviders] = useState({
    [VIDEO_PROVIDER.ZOOM]: false,
    [VIDEO_PROVIDER.ZOHO]: false,
    [VIDEO_PROVIDER.GOOGLE]: false,
  });
  const [creatingSession, setCreatingSession] = useState(false);

  const [form, setForm] = useState({
    razorpayAccountName: "",
    razorpayKeyId: "",
    razorpayKeySecret: "",
    razorpayWebhookSecret: "",
    businessName: "",
    businessAddress: "",
    gstin: "",
    cin: "",
    businessEmail: "",
    businessPhone: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [profileExists, setProfileExists] = useState(false);

  // Utility to convert 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "10:30 AM"; // default
    const [hoursStr, minutes] = time24.split(":");
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const buildEventStartIso = useCallback(() => {
    if (!startDate) return null;
    const timePart = eventTime && String(eventTime).trim()
      ? String(eventTime).trim().slice(0, 8)
      : "00:00:00";
    const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
    const combined = new Date(`${startDate}T${normalizedTime}`);
    if (Number.isNaN(combined.getTime())) return null;
    return combined.toISOString();
  }, [startDate, eventTime]);

  const refreshProviderConnections = useCallback(async () => {
    const [zoomStatus, googleStatus, zohoStatus] = await Promise.all([
      ZoomServices.getConnectionStatus().catch(() => null),
      GoogleMeetServices.getConnectionStatus().catch(() => null),
      ZohoMeetingServices.getConnectionStatus().catch(() => null),
    ]);

    setConnectedProviders({
      [VIDEO_PROVIDER.ZOOM]: Boolean(zoomStatus?.connected),
      [VIDEO_PROVIDER.GOOGLE]: Boolean(googleStatus?.connected),
      [VIDEO_PROVIDER.ZOHO]: Boolean(zohoStatus?.connected),
    });
  }, []);

const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
  fetchedRef.current = true;
    fetchProfile();
    refreshProviderConnections();
  }, [refreshProviderConnections]);

  const availableProviders = VIDEO_PROVIDERS.filter(
    (provider) =>
      sessionType !== SESSION_TYPE.WEBINAR ||
      provider.key !== VIDEO_PROVIDER.GOOGLE,
  );

  useEffect(() => {
    if (
      sessionType === SESSION_TYPE.WEBINAR &&
      videoProvider === VIDEO_PROVIDER.GOOGLE
    ) {
      setVideoProvider(VIDEO_PROVIDER.ZOOM);
    }
  }, [sessionType, videoProvider]);

  const isSelectedProviderConnected = Boolean(connectedProviders[videoProvider]);

  const getProviderLabel = (providerKey) =>
    VIDEO_PROVIDERS.find((provider) => provider.key === providerKey)?.label || providerKey;

  const createSessionForProvider = async (payload) => {
    if (videoProvider === VIDEO_PROVIDER.ZOOM) {
      return sessionType === SESSION_TYPE.WEBINAR
        ? ZoomServices.createWebinar(payload)
        : ZoomServices.createUserMeeting(payload);
    }

    if (videoProvider === VIDEO_PROVIDER.GOOGLE) {
      return GoogleMeetServices.createUserMeeting({
        topic: payload.topic,
        isInstant: false,
        startTime: payload.startTime,
        duration: payload.duration,
      });
    }

    if (videoProvider === VIDEO_PROVIDER.ZOHO) {
      return sessionType === SESSION_TYPE.WEBINAR
        ? ZohoMeetingServices.createWebinar(payload)
        : ZohoMeetingServices.createEventMeeting(payload);
    }

    throw new Error("Unsupported provider selected.");
  };

  const handleCreateSession = async () => {
    if (!isSelectedProviderConnected) return;

    if (!campaignName?.trim()) {
      await showWarningAlert("Required", SESSION_MESSAGES.EVENT_NAME_REQUIRED);
      return;
    }

    const startIso = buildEventStartIso();
    if (!startIso) {
      await showWarningAlert("Required", SESSION_MESSAGES.START_DATE_TIME_REQUIRED);
      return;
    }

    const duration = Number(durationInMinutes);
    if (!duration || duration <= 0) {
      await showWarningAlert("Required", SESSION_MESSAGES.DURATION_REQUIRED);
      return;
    }

    const sessionLabel =
      sessionType === SESSION_TYPE.WEBINAR
        ? SESSION_TYPE.WEBINAR
        : SESSION_TYPE.MEETING;
    const providerLabel = getProviderLabel(videoProvider);

    try {
      setCreatingSession(true);
      const response =
        sessionType === SESSION_TYPE.WEBINAR
          ? await createSessionForProvider({
              eventId: String(campaignId),
              topic: campaignName.trim(),
              startTime: startIso,
              durationInMinutes: duration,
              agenda: description || null,
              guestEmails: null,
            })
          : await createSessionForProvider({
              eventId: String(campaignId),
              topic: campaignName.trim(),
              startTime: startIso,
              duration: duration,
              agenda: description || null,
              guestEmails: null,
            });

      const meetingUrl = extractJoinUrl(response);
      if (!meetingUrl) {
        await showWarningAlert(
          "Missing link",
          SESSION_MESSAGES.NO_MEETING_LINK(providerLabel, sessionLabel),
        );
        return;
      }

      updateEventDetails({ videoConferenceLink: meetingUrl });
      await showSuccessAlert(
        "Created",
        SESSION_MESSAGES.LINK_FILLED(providerLabel, sessionLabel),
      );
    } catch (err) {
      console.error(err);
      await showErrorAlert(
        "Create failed",
        err?.response?.data?.message ||
          err?.message ||
          SESSION_MESSAGES.CREATE_FAILED(providerLabel, sessionLabel),
      );
    } finally {
      setCreatingSession(false);
    }
  };

  const hasStoredRazorpayData = (d) =>
    d &&
    (String(d.razorpayKeyId || "").trim() ||
      String(d.razorpayAccountName || "").trim());

  const fetchProfile = async () => {
    try {
      const resp = await EventService.getUserPaymentProfile();
      if (resp && hasStoredRazorpayData(resp)) {
        const d = resp;

        setProfileExists(true);
        setIsEditingPayment(false);

        setForm({
          razorpayAccountName: d.razorpayAccountName || "",
          razorpayKeyId: d.razorpayKeyId || "",
          razorpayKeySecret: d.razorpayKeySecret || "",
          razorpayWebhookSecret: d.razorpayWebhookPathSegment || "",
          businessName: d.businessName || "",
          businessAddress: d.businessAddress || "",
          gstin: d.gstin || "",
          cin: d.cin || "",
          businessEmail: d.businessEmail || "",
          businessPhone: d.businessPhone || "",
        });

        if (d.logoUrl) setLogoPreview(d.logoUrl);
      } else {
        setProfileExists(false);
        setIsEditingPayment(false);
      }
    } catch (err) {
      console.log("No existing Razorpay profile");
      setProfileExists(false);
      setIsEditingPayment(false);
    }
  };

  const updateField = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      showWarningAlert("Invalid file", "Only PNG/JPG/WEBP allowed");
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleIntegrate = async (e) => {
    e.preventDefault();

     if (!validatePaymentFields()) return;

    const fd = new FormData();

    Object.keys(form).forEach(k => {
      fd.append(k.charAt(0).toUpperCase() + k.slice(1), form[k]);
    });

    if (logoFile) fd.append("LogoFile", logoFile);

    try {
      const resp = profileExists
        ? await EventService.updateUserPaymentProfile(fd)
        : await EventService.createUserPaymentProfile(fd);

      if (resp.success) {
        await showSuccessAlert("Saved", "Saved successfully!");
        setProfileExists(true);
        setIsEditingPayment(false);
        fetchProfile();
      }
    } catch (err) {
      await showErrorAlert("Save failed", "Failed to save");
    }
  };

  const paymentFieldsLocked = profileExists && !isEditingPayment;

  const handleDeletePayment = async () => {
    const confirmed = await showDeleteConfirm({
      title: "Remove payment profile?",
      text: "Remove your saved Razorpay profile? You will need to integrate again to collect payments.",
      confirmButtonText: "Remove",
    });
    if (!confirmed) {
      return;
    }
    try {
      const resp = await EventService.deleteUserPaymentProfile();
      if (resp && resp.success === false) {
        await showErrorAlert(
          "Remove failed",
          resp.message || "Failed to remove payment profile.",
        );
        return;
      }
      setProfileExists(false);
      setIsEditingPayment(false);
      setLogoFile(null);
      setLogoPreview(null);
      setForm({
        razorpayAccountName: "",
        razorpayKeyId: "",
        razorpayKeySecret: "",
        razorpayWebhookSecret: "",
        businessName: "",
        businessAddress: "",
        gstin: "",
        cin: "",
        businessEmail: "",
        businessPhone: "",
      });
      await showSuccessAlert("Removed", "Payment profile removed.");
    } catch (err) {
      console.error(err);
      await showErrorAlert("Remove failed", "Failed to remove payment profile.");
    }
  };

  const validateFinalTouchUp = () => {
  if (!profileExists) {
    showWarningAlert("Payment required", "Please integrate payment gateway first.");
    return false;
  }

  return true;
};


  const generateWebhookSecret = () => {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, "0")).join("");
  };

  // ✅ Update shared campaignData when conference link changes
  const handleConferenceChange = (e) => {
    updateEventDetails((prev) => ({
      videoConferenceLink: e.target.value,
    }));
  };

  // ✅ Save & Continue (move to Preview tab)
  const handleContinue = () => {
    if (!validateFinalTouchUp()) return;
    console.log('Final campaign data before continue:', campaignData);
    if (onNext) onNext(); // Navigate to Preview tab
  };

  const validatePaymentFields = () => {
  const missing = [];

  if (!form.razorpayAccountName?.trim())
    missing.push("Account Name");

  if (!form.razorpayKeyId?.trim())
    missing.push("Key ID");

  if (!form.razorpayKeySecret?.trim())
    missing.push("Key Secret");

  if (!form.razorpayWebhookSecret?.trim())
    missing.push("Webhook Secret");

  if (!form.businessName?.trim())
    missing.push("Business Name");

  if (!form.businessAddress?.trim())
    missing.push("Business Address");

  if (!form.businessEmail?.trim())
    missing.push("Business Email");

  if (!form.businessPhone?.trim())
    missing.push("Business Phone");

  if (!form.gstin?.trim())
    missing.push("GSTIN");

  if (!form.cin?.trim())
    missing.push("CIN");

  if (missing.length) {
    showWarningAlert(
      "Missing fields",
      `Please fill the following:\n\n${missing.join("\n")}`,
    );
    return false;
  }

  return true;
};


  const handleSave = async () => {
    // Collect missing fields (excluding meetingLink)
    const missingFields = [];

    if (!campaignName) missingFields.push("Event Name");
    if (!description) missingFields.push("Event Description");
    if (!startDate) missingFields.push("Start Date");
    if (!durationInMinutes || Number(durationInMinutes) <= 0) {
      missingFields.push("Duration (In minutes)");
    }
    if (!eventTime) missingFields.push("Event Time");
    if (!eventPriceTagLine) missingFields.push("Price Tag Line");
    if (!eventPrice) missingFields.push("Event Price");
    if (!formFields?.length) missingFields.push("Form Fields");

    if (missingFields.length > 0) {
      await showWarningAlert(
        "Missing fields",
        `Please fill in the following fields before saving:\n\n${missingFields.join(", ")}`,
      );
      return;
    }

    const payload = {
      eventId: campaignId,
      eventName: campaignName,
      eventDetails: description,
      startDate: new Date(startDate).toISOString(),
      durationInMinutes: Number(durationInMinutes) || 30,
      isPreview: false,
      isDraft: isDraft,
      eventTime: formatTimeTo12Hour(eventTime) || "10:30 AM",
      eventPriceTagLine: eventPriceTagLine,
      eventEnterPrice: parseFloat(eventPrice) || 0,
      eventObjective: templateCategory,
      templateId: templateId,
      eventTemplateId: eventTemplateId,
      templateSummary: templateSummary,
      templateBody: templateHtml,
      templateImage: templateImage,
      eventInputs: formFields.map((field) => ({
        inputTitle: field.label,
        isRequire: field.required,
      })),
      meetingLink: videoConferenceLink, // optional
    };

    try {
      const response = await EventService.saveEvent(payload);
      if (response.data?.success) {
        const ev = response.data.data;
        updateEventDetails(
          {
            campaignId: ev?.id || campaignId,
            templateId: ev?.eventTemplate?.templateId || templateId,
            eventTemplateId: ev?.eventTemplate?.id || eventTemplateId,
            templateHtml: ev?.eventTemplate?.templateBody || templateHtml,
            templateSummary: ev?.eventTemplate?.templateSummary || templateSummary,
            templateCategory: templateCategory,
            templateImage: ev?.eventTemplate?.templateImage || templateImage,
            isDraft: ev?.isDraft ?? isDraft,
          },
          false,
        );
        markSaved();
        await showSuccessAlert("Saved", "Event saved successfully!");
      } else {
        console.error("API Error:", response.data);
        await showErrorAlert("Save failed", "Failed to save event.");
      }
    } catch (error) {
      console.error("Request failed:", error);
      await showErrorAlert(
        "Save failed",
        "An error occurred while saving the event.",
      );
    }
  };
  
  useEffect(() => {
  if (registerValidator) {
    registerValidator(validateFinalTouchUp);
  }
}, [profileExists, validateFinalTouchUp]);


  return (
    <div className="p-4">
      {/* Create webinar / meeting */}
      {FeatureFlagService.isEnabled(FeatureFlags.WEBINAR_OR_MEETINGS_CREATION_EVENTS) && (
        <>
          <div className="mb-4">
            <h6 className="fw-bold mb-2">Create using :</h6>

            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              {SESSION_TYPES.map(({ key, label }) => (
                <div className="form-check mb-0" key={key}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sessionType"
                    id={`session-${key}`}
                    checked={sessionType === key}
                    onChange={() => setSessionType(key)}
                  />
                  <label className="form-check-label" htmlFor={`session-${key}`}>
                    {label}
                  </label>
                </div>
              ))}
            </div>

            <div className="d-flex flex-wrap align-items-center gap-3">
              {availableProviders.map(({ key, label }) => (
                <div className="form-check mb-0" key={key}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="videoProvider"
                    id={`provider-${key}`}
                    checked={videoProvider === key}
                    onChange={() => setVideoProvider(key)}
                  />
                  <label className="form-check-label" htmlFor={`provider-${key}`}>
                    {label}{connectedProviders[key] ? "" : " (not connected)"}
                  </label>
                </div>
              ))}

              <PrimaryButton
                type="button"
                onClick={handleCreateSession}
                disabled={!isSelectedProviderConnected || creatingSession}
              >
                {creatingSession ? "Creating..." : "Create"}
              </PrimaryButton>
            </div>

            {!isSelectedProviderConnected && (
              <p className="text-muted small mt-2 mb-0">
                Connect {getProviderLabel(videoProvider)} under{" "}
                <a href="/video-conf">Video Conf</a> to enable Create.
              </p>
            )}
          </div>

          {/* Video Conference */}
          <div className="mt-4">
            <ReusableTextField
              name="conferenceLink"
              label="Video Conference Link"
              placeholder="Enter the Video Conference link here and keep it ready. Once the user
          registers, the link will be sent to them via Email/WhatsApp."
              value={videoConferenceLink || ''}
              onChange={handleConferenceChange}
              fullWidth
            />
          </div>
        </>
      )}

      <br />

      {/* Integrate Email */}
      <div className="mb-4">
        <h6 className="fw-bold mb-2">Integrate Email</h6>
        <p className="mb-1">
          Looks like you have not integrated your email yet. Please integrate it
          now. <a href="/email">Click here</a>.
        </p>
      </div>

      <br />

      {/* Payment Gateway */}
      <div>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
          <div>
            <h6 className="fw-bold mb-2">Payment Gateway</h6>
            <p className="text-muted mb-0">
              Integrate your payment system to get the amount directly to your
              account when someone registers.
            </p>
          </div>
          {profileExists && (
            <div className="d-flex flex-shrink-0 gap-2 align-items-center pt-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setIsEditingPayment(true)}
                disabled={isEditingPayment}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleDeletePayment}
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {profileExists && (
          <div className="mb-3">
            <p>Already have a Razorpay account?</p>

            <div className="form-check">
              <input type="radio" checked readOnly />
              <label>{form.razorpayAccountName}</label>
            </div>
          </div>
        )}


        <div className="border rounded p-3">
          <p className="small mb-3">
            We will need your API Key ID and API Key Secret (private key) to
            integrate your payment system. We will need this info only once.
          </p>
          <form onSubmit={handleIntegrate}>
            <ReusableTextField
              label="Account Name"
              value={form.razorpayAccountName}
              onChange={updateField("razorpayAccountName")}
              disabled={paymentFieldsLocked}
            />

            <ReusableTextField
              label="Key ID"
              value={form.razorpayKeyId}
              onChange={updateField("razorpayKeyId")}
              disabled={paymentFieldsLocked}
            />

            <ReusableTextField
              label="Key Secret"
              value={form.razorpayKeySecret}
              onChange={updateField("razorpayKeySecret")}
              disabled={paymentFieldsLocked}
            />

            <div>

              {/* Header Row */}
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label mb-0 fw-semibold">
                  Webhook Secret
                </label>

                <div className="d-flex gap-2">

                  <button
                    type="button"
                    onClick={() =>
                      setForm(prev => ({
                        ...prev,
                        razorpayWebhookSecret: generateWebhookSecret()
                      }))
                    }
                    className="btn btn-sm btn-light border"
                    disabled={paymentFieldsLocked}
                  >
                    Regenerate
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(form.razorpayWebhookSecret);
                    }}
                    className="btn btn-sm btn-light border"
                  >
                    Copy
                  </button>

                </div>
              </div>

              {/* Input Field */}
              <ReusableTextField
                label=""
                value={form.razorpayWebhookSecret}
                onChange={() => {}}
                disabled
              />

            </div>


            <ReusableTextField
              label="Business Name"
              value={form.businessName}
              onChange={updateField("businessName")}
              disabled={paymentFieldsLocked}
            />

            <ReusableTextField
              label="Business Address"
              value={form.businessAddress}
              onChange={updateField("businessAddress")}
              disabled={paymentFieldsLocked}
            />


            <ReusableTextField
              label="Business Email"
              value={form.businessEmail}
              onChange={updateField("businessEmail")}
              disabled={paymentFieldsLocked}
            />

            <ReusableTextField
              label="Business Phone"
              value={form.businessPhone}
              onChange={updateField("businessPhone")}
              disabled={paymentFieldsLocked}
            />

            <ReusableTextField
              label="GSTIN"
              value={form.gstin}
              onChange={updateField("gstin")}
              disabled={paymentFieldsLocked}
            />

            <ReusableTextField
              label="CIN"
              value={form.cin}
              onChange={updateField("cin")}
              disabled={paymentFieldsLocked}
            />

            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={paymentFieldsLocked}
            />

            {logoPreview && (
              <img src={logoPreview} width="120" alt="logo" />
            )}


            {!paymentFieldsLocked && (
              <div className="d-flex justify-content-end integrate-btn">
                <PrimaryButton type="submit" disabled={saving}>
                  {saving
                    ? "Saving..."
                    : profileExists
                      ? "Update integration"
                      : "Integrate"}
                </PrimaryButton>
              </div>
            )}

            <p className="small text-muted mt-3">
              By submitting, you are giving ProspectPulse (Nurturix) consent to
              store this info with ProspectPulse. ProspectPulse stores this info
              using secure encryption and will not share it externally. Please
              check our <a href="/privacy-policy">Data consent and privacy</a>{' '}
              policy.
            </p>
          </form>
        </div>
      </div>

      {/* ✅ Updated Bottom Buttons: Same layout as AddDetailsTab */}
      <div className="action-buttons">
        <button className="btn secondary go-back" onClick={onBack}>
          Back
        </button>
        <div className="right-actions">
          <button className="btn warn save" onClick={handleSave} disabled={!isDraft}>
            Save as Draft
          </button>
          <button
            className="btn success continue"
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalTouchUpTab;